import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { toast } from 'sonner';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, X, Star, Share2, Copy, Package } from 'lucide-react';
import { shareCatalog, fetchShared, applyShared } from '../lib/catalogShare';
import { branchOn, subOn, usePolicy } from '../lib/policy';
import { PROFILES, DETECT_SUBS, getMode, getSub, applyProfiles, needsOnboarding, openProfiles, useMode, modeId, getStart, setStart, startLink, readFavs, getPending, acceptPending } from '../lib/profiles';

// single compact "Modus: …" bar at the top of the start page (shows the active sub-mode directly)
export function ModeBar({ t }) {
  const id = useMode(), [mode, sub] = id.split('_');
  return (
    <button className={`mode-bar mode-${mode}`} data-testid="mode-bar-btn" onClick={openProfiles}>
      <span className="mode-ic">{sub ? DETECT_SUBS[sub].icon : PROFILES[mode].icon}</span>
      <span className="mode-txt"><small>{t.mbMode}</small><b data-testid="mode-bar-btn-label">{sub ? t['dsub_' + sub] : t['pf_' + mode]}</b></span>
      <ChevronDown size={16} />
    </button>
  );
}

const modeName = (t, id) => { const [m, s] = id.split('_'); return s ? t['dsub_' + s] : t['pf_' + m]; };

// save the current mode as crew start mode + share it as link / QR for new phones
function StartShare({ t, kritis }) {
  const [st, setSt] = useState(getStart);
  const [qr, setQr] = useState(null);
  const [cat, setCat] = useState(null);
  const [busy, setBusy] = useState(false);
  const id = modeId(), favs = st ? readFavs()[st] || [] : [], link = startLink(st || id, favs, cat);
  const toggleCat = async () => {
    if (cat) return setCat(null);
    setBusy(true);
    try { setCat((await shareCatalog()).code); } catch { toast.error(t.tpCatErr); }
    setBusy(false);
  };
  useEffect(() => { if (st) QRCode.toDataURL(link, { width: 360, margin: 2 }).then(setQr); else setQr(null); }, [st, link]);
  const save = () => { setStart(id); setSt(id); toast.success(t.smSaved); };
  const share = async () => { try { if (navigator.share) await navigator.share({ title: 'GISS FieldRapport Pro', text: `${t.smShareText}: ${modeName(t, st)}`, url: link }); else { await navigator.clipboard.writeText(link); toast.success(t.shCopied); } } catch { /* cancelled */ } };
  return (
    <div className="sm-box" data-testid="start-mode-box">
      <div className="row">
        <button className="btn btn-dark btn-sm" data-testid="start-mode-save-btn" onClick={save}><Star size={14} /> {t.smSave}</button>
        {st && <span className="muted" data-testid="start-mode-current">⭐ {t.smCurrent}: <b>{modeName(t, st)}</b></span>}
        {st && <button className="btn btn-outline btn-sm" data-testid="start-mode-clear-btn" onClick={() => { setStart(null); setSt(null); }}><X size={14} /> {t.smClear}</button>}
      </div>
      {st && (
        <div className="row sm-share">
          {qr && <img src={qr} alt="QR" className="sm-qr" data-testid="start-mode-qr" />}
          <div className="sm-link">
            <small>{t.smHint}</small>
            {favs.length > 0 && <small data-testid="start-mode-favs">⭐ {t.smFavs.replace('{n}', favs.length)}</small>}
            {!kritis && <label className="tp-cat" data-testid="team-cat-toggle"><input type="checkbox" data-testid="team-cat-checkbox" checked={!!cat} disabled={busy} onChange={toggleCat} /> <Package size={14} /> {busy ? t.tpCatBusy : cat ? t.tpCatOn.replace('{c}', cat) : t.tpCat}</label>}
            <code data-testid="start-mode-link">{link}</code>
            <div className="row">
              <button className="btn btn-share btn-sm" data-testid="start-mode-share-btn" onClick={share}><Share2 size={14} /> {t.shBtn}</button>
              <button className="btn btn-outline btn-sm" data-testid="start-mode-copy-btn" onClick={() => navigator.clipboard?.writeText(link).then(() => toast.success(t.shCopied))}><Copy size={14} /> {t.shCopy}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// start-mode link / QR opened: confirm before mode + pinned tools are applied
export function StartLinkPrompt({ t }) {
  const [p, setP] = useState(getPending);
  const [merge, setMerge] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);
  if (!p) return null;
  const done = async (ok, withCat = true) => {
    let raw = null;
    if (ok && p.cat && withCat) {
      setBusy(true);
      try { raw = await fetchShared(p.cat); } catch { setBusy(false); setErr(true); toast.error(t.tpLoadErr); return; }
      setBusy(false);
    }
    acceptPending(ok); setP(null);
    if (raw) applyShared(raw, merge);
    if (ok) toast.success(raw ? t.tpDone.replace('{n}', (raw.items || []).length) : t.slDone); else if (needsOnboarding()) openProfiles();
  };
  return createPortal(
    <div className="editor-backdrop" data-testid="start-link-prompt">
      <div className="editor sl-box">
        <h4>📲 {p.cat ? t.tpTitle : t.stlTitle}</h4>
        <p data-testid="start-link-mode">{t.slMode}: <b>{modeName(t, p.mode)}</b></p>
        {p.fav.length > 0 && <p data-testid="start-link-favs">⭐ {t.slFavs.replace('{n}', p.fav.length)}</p>}
        {p.cat && <p data-testid="start-link-cat">📦 {t.tpCatIn.replace('{c}', p.cat)}</p>}
        {p.cat && <label className="tp-cat"><input type="checkbox" data-testid="start-link-merge" checked={merge} onChange={(e) => setMerge(e.target.checked)} /> {t.tpMerge}</label>}
        <div className="row">
          <button className="btn btn-green" data-testid="start-link-accept-btn" disabled={busy} onClick={() => done(true)}><Check size={16} /> {busy ? t.tpCatBusy : t.slOk}</button>
          {err && <button className="btn btn-outline" data-testid="start-link-nocat-btn" onClick={() => done(true, false)}>{t.tpNoCat}</button>}
          <button className="btn btn-outline" data-testid="start-link-decline-btn" disabled={busy} onClick={() => done(false)}><X size={16} /> {t.slNo}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// sector cards: one tap switches the whole UI
export default function ProfilePicker({ t }) {
  const [open, setOpen] = useState(() => needsOnboarding() && !getPending() && !!localStorage.getItem('giss_setup_done'));
  const [cur, setCur] = useState(getMode);
  const { kritis } = usePolicy();
  useEffect(() => { const on = () => { setCur(getMode()); setOpen(true); }; window.addEventListener('giss-profile-open', on); return () => window.removeEventListener('giss-profile-open', on); }, []);
  useEffect(() => { const on = () => {}; window.addEventListener('giss-setup-done', on); return () => window.removeEventListener('giss-setup-done', on); }, []);
  if (!open) return null;
  const pick = (k, sub) => { applyProfiles([k], sub); setOpen(false); };
  return createPortal(
    <div className="editor-backdrop" data-testid="profile-picker" onClick={(e) => e.target === e.currentTarget && setOpen(false)}>
      <div className="editor profile-picker">
        <div className="row pf-head"><h4>🎯 {t.pfTitle}</h4><button className="icon-btn" data-testid="profile-close-btn" onClick={() => { if (needsOnboarding()) applyProfiles([cur]); setOpen(false); }}><X size={18} /></button></div>
        <p className="muted small-hint">{t.mbHint}</p>
        <div className="pf-grid">
          {Object.entries(PROFILES).filter(([k]) => branchOn(k)).map(([k, p]) => (
            <div key={k} role="button" tabIndex={0} className={`pf-card ${cur === k ? 'on' : ''}`} data-testid={`profile-card-${k}`} aria-pressed={cur === k} onClick={() => pick(k)}>
              <span className="pf-ic">{p.icon}</span><b>{t['pf_' + k]}</b><small>{t['pfD_' + k]}</small>{cur === k && <Check size={16} className="pf-check" />}
              {k === 'detect' && (
                <span className="pf-subs">
                  {Object.entries(DETECT_SUBS).filter(([s]) => subOn(s)).map(([s, d]) => <button key={s} className={`pf-sub ${cur === 'detect' && getSub() === s ? 'on' : ''}`} data-testid={`profile-sub-${s}`} onClick={(e) => { e.stopPropagation(); pick('detect', s); }}>{d.icon} {t['dsub_' + s]}</button>)}
                </span>
              )}
            </div>
          ))}
        </div>
        <StartShare t={t} kritis={kritis} />
      </div>
    </div>,
    document.body,
  );
}
